import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from './base.api';
import {
	CertificationSubmitRequestDto,
	CertificationSubmitDto,
	AdminProfileCertificationDto,
	ProfileCertificationDto,
	CertificationUpdateRequestDto,
	SustainabilityCertificationDto,
	sortProfileCertificationsByCreatedAt
} from '../../models/certification';
import HttpClient from '../../services/http-client/http-client';

const baseUrl = `${process.env.REACT_APP_PROFILE_URL}/api/v2/certification`;

/** Backend accepts optional profileSustainabilityFormId only when it is greater than 0. */
export function isScopedProfileSustainabilityFormId(
	id: number | undefined | null
): id is number {
	return typeof id === 'number' && id > 0;
}

const appendCertificationIdentityFields = (
	formData: FormData,
	request: CertificationSubmitRequestDto | CertificationUpdateRequestDto
) => {
	formData.append('ProfileSustainabilityFormId', String(request.profileSustainabilityFormId));
	if (request.file) formData.append('File', request.file);
	if ('removeDocument' in request && request.removeDocument) {
		formData.append('RemoveDocument', 'true');
	}
	if (request.journeyAnswer) formData.append('JourneyAnswer', request.journeyAnswer);

	if (request.certId) {
		formData.append('CertId', String(request.certId));
		return;
	}

	if (request.customSchemeName) {
		formData.append('CustomSchemeName', request.customSchemeName);
	}
};

export const certificationApi = createApi({
	reducerPath: 'certificationApi',
	baseQuery: axiosBaseQuery(baseUrl),
	// Tags identify cached data so we can invalidate it: when a mutation invalidates a tag,
	// any query that provides that tag will refetch. Keeps the UI in sync after create/update/delete.
	tagTypes: ['certification'],
	endpoints: (builder) => ({
		getProfileCertifications: builder.query<
			ProfileCertificationDto[],
			{
				profileId: number;
				index?: number;
				total?: number;
				searchText?: string;
				profileSustainabilityFormId?: number;
			}
		>({
			query: ({ profileId, index = 0, total = 10, searchText = '', profileSustainabilityFormId }) => ({
				url: `/${profileId}/profile-certifications`,
				method: 'GET',
				params: {
					index,
					total,
					searchText,
					...(isScopedProfileSustainabilityFormId(profileSustainabilityFormId)
						? { profileSustainabilityFormId }
						: {})
				}
			}),
			transformResponse: (response: ProfileCertificationDto[]) =>
				sortProfileCertificationsByCreatedAt(response ?? []),
			providesTags: [{ type: 'certification', id: 'LIST' }]
		}),
		getAllProfileCertificationsPage: builder.query<
			AdminProfileCertificationDto[],
			{ index?: number; total?: number; searchText?: string; status?: number | null }
		>({
			query: ({ index = 0, total = 10, searchText = '', status = null }) => ({
				url: `/profile-certifications`,
				method: 'GET',
				params: {
					index,
					total,
					searchText,
					...(status === null || status === undefined ? {} : { status })
				}
			}),
			providesTags: [{ type: 'certification', id: 'ADMIN_LIST' }]
		}),

		submitCertification: builder.mutation<
			CertificationSubmitDto,
			{
				profileId: number;
				request: CertificationSubmitRequestDto;
				onProgress?: (percent: number) => void;
			}
		>({
			queryFn: async ({ profileId, request, onProgress }) => {
				const formData = new FormData();
				appendCertificationIdentityFields(formData, request);

				try {
					const result = await HttpClient.post(
						baseUrl,
						`/${profileId}/certification/submit`,
						formData,
						{ 'Content-Type': 'multipart/form-data' },
						{
							onUploadProgress: onProgress
								? (e: ProgressEvent) => {
										if (e.total) onProgress((e.loaded / e.total) * 100);
								  }
								: undefined
						}
					);
					return { data: result.data as CertificationSubmitDto };
				} catch (error) {
					return { error: error as { status?: number; data?: unknown } };
				}
			},
			invalidatesTags: (_result, _error) => [{ type: 'certification', id: 'LIST' }]
		}),
		deleteCertification: builder.mutation<
			void,
			{ profileId: number; profileCertificationId: number }
		>({
			query: ({ profileId, profileCertificationId }) => ({
				url: `/${profileId}/certification`,
				method: 'DELETE',
				params: { profileCertificationId }
			}),
			invalidatesTags: (_result, _error) => [{ type: 'certification', id: 'LIST' }]
		}),
		updateCertification: builder.mutation<
			ProfileCertificationDto,
			{
				profileId: number;
				profileCertId: number;
				request: CertificationUpdateRequestDto;
				onProgress?: (percent: number) => void;
			}
		>({
			queryFn: async ({ profileId, profileCertId, request, onProgress }) => {
				const formData = new FormData();
				appendCertificationIdentityFields(formData, request);

				try {
					const result = await HttpClient.put(
						baseUrl,
						`/${profileId}/certification/${profileCertId}`,
						formData,
						{ 'Content-Type': 'multipart/form-data' },
						{
							onUploadProgress: onProgress
								? (e: ProgressEvent) => {
										if (e.total) onProgress((e.loaded / e.total) * 100);
								  }
								: undefined
						}
					);
					return { data: result.data as ProfileCertificationDto };
				} catch (error) {
					return { error: error as { status?: number; data?: unknown } };
				}
			},
			invalidatesTags: (_result, _error) => [{ type: 'certification', id: 'LIST' }]
		}),
		setCertificationStatus: builder.mutation<
			ProfileCertificationDto,
			{ profileId: number; profileCertificationId: number; status: number }
		>({
			query: ({ profileId, profileCertificationId, status }) => ({
				url: `/${profileId}/certification/set-status`,
				method: 'PUT',
				params: { profileCertificationId, status }
			}),
			invalidatesTags: (_result, _error) => [
				{ type: 'certification', id: 'LIST' },
				{ type: 'certification', id: 'ADMIN_LIST' }
			]
		}),

		getCertifications: builder.query<
			SustainabilityCertificationDto[],
			{ index?: number; total?: number; searchText?: string }
		>({
			query: ({ index = 0, total = 10, searchText = '' }) => ({
				url: `/certifications`,
				method: 'GET',
				params: { index, total, searchText }
			}),
			providesTags: [{ type: 'certification', id: 'BODY_LIST' }]
		})
	})
});

export const {
	useGetProfileCertificationsQuery,
	useGetAllProfileCertificationsPageQuery,
	useSubmitCertificationMutation,
	useDeleteCertificationMutation,
	useUpdateCertificationMutation,
	useSetCertificationStatusMutation,
	useGetCertificationsQuery,
	useLazyGetCertificationsQuery
} = certificationApi;

export default certificationApi;
